import React, { useState, useRef, useEffect } from "react";
import "./AccountDeletion.css";
import { post } from "../../../api/api";

const AccountDeletion = () => {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const topRef = useRef(null);

  // Scroll to top when component mounts or when submitted state changes
  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [submitted]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      alert("Please enter your email address.");
      return;
    }

    setLoading(true);

    try {
      await post("/users/delete/request-account-deletion", {
        email,
        reason,
        feedback,
      });
      setSubmitted(true);
      // Scroll to top after successful submission
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      alert("Something went wrong. Please try again or contact support.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="legal-page" ref={topRef}>
        <section className="legal-hero">
          <div className="container">
            <h1>Deletion Request Submitted</h1>
            <p className="legal-subtitle">
              We've received your account deletion request.
            </p>
          </div>
        </section>

        <section className="legal-content">
          <div className="container">
            <div className="deletion-success">
              <div className="success-icon">✅</div>
              <h2>Check Your Email</h2>
              <p>
                If your email address is registered with GoalFlex, you'll
                receive a confirmation link within the next few minutes. Please
                check your email and follow the instructions to complete the
                deletion process.
              </p>

              <div className="next-steps">
                <h3>What happens next?</h3>
                <ul>
                  <li>
                    You'll receive a confirmation email with a secure deletion
                    link
                  </li>
                  <li>Click the link to confirm your deletion request</li>
                  <li>
                    Your account and data will be permanently deleted within 30
                    days
                  </li>
                  <li>
                    You'll receive a final confirmation when deletion is
                    complete
                  </li>
                </ul>
              </div>

              <div className="help-section">
                <p>
                  <strong>Didn't receive the email?</strong> Check your spam
                  folder or contact us at{" "}
                  <a href="mailto:support@goalflex.com">support@goalflex.com</a>
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="legal-page" ref={topRef}>
      {/* Hero Section */}
      <section className="legal-hero">
        <div className="container">
          <h1>Delete Your Account</h1>
          <p className="legal-subtitle">
            We're sorry to see you go. Please complete the form below to request
            account deletion.
          </p>
        </div>
      </section>

      {/* Content Section - Form First */}
      <section className="legal-content">
        <div className="container">
          {/* Account Deletion Form - Now First */}
          <div className="deletion-form-section priority-section">
            <h2>Account Deletion Request</h2>
            <p className="form-intro">
              Please fill out the form below to request deletion of your
              GoalFlex account. We'll send you a confirmation email to complete
              the process.
            </p>

            <form onSubmit={handleSubmit} className="deletion-form">
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  className="form-input"
                  placeholder="Enter your registered email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <p className="form-help">
                  We'll send a confirmation link to this email address.
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="reason">Reason for Leaving (Optional)</label>
                <select
                  id="reason"
                  className="form-select"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                >
                  <option value="">Select a reason...</option>
                  <option value="not-using">
                    Not using the service anymore
                  </option>
                  <option value="found-alternative">
                    Found a better alternative
                  </option>
                  <option value="privacy-concerns">Privacy concerns</option>
                  <option value="too-expensive">Too expensive</option>
                  <option value="technical-issues">Technical issues</option>
                  <option value="safety-concerns">Safety concerns</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="feedback">Additional Feedback (Optional)</label>
                <textarea
                  id="feedback"
                  className="form-textarea"
                  placeholder="Help us improve by sharing your experience..."
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  rows="4"
                />
                <p className="form-help">
                  Your feedback helps us improve the platform for other users.
                </p>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-delete" disabled={loading}>
                  {loading ? "Processing..." : "Request Account Deletion"}
                </button>

                <div className="alternative-actions">
                  <p>
                    <strong>Need help instead?</strong>{" "}
                    <a href="mailto:support@goalflex.com">Contact Support</a>
                  </p>
                  <p>
                    <strong>Just taking a break?</strong>{" "}
                    <a href="/contact">Deactivate temporarily</a>
                  </p>
                </div>
              </div>
            </form>
          </div>

          {/* Important Information Section - Now Second */}
          <div className="deletion-warning">
            <h2>⚠️ Important Information</h2>
            <p>
              Deleting your account will permanently remove all your data,
              including:
            </p>
            <ul>
              <li>Your profile and account information</li>
              <li>Game history and bookings</li>
              <li>Ratings and reviews</li>
              <li>Messages and communications</li>
              <li>
                Payment history (for tax purposes, some records may be retained)
              </li>
            </ul>
            <p>
              <strong>This action cannot be reversed.</strong> If you're having
              issues with the platform, please consider contacting our support
              team first.
            </p>
          </div>

          <div className="legal-section">
            <h2>Data Retention Policy</h2>
            <p>After you confirm your deletion request:</p>
            <ul>
              <li>
                <strong>Immediate:</strong> Your account will be deactivated and
                hidden from other users
              </li>
              <li>
                <strong>7 days:</strong> Personal data removal begins
              </li>
              <li>
                <strong>30 days:</strong> Complete data deletion (except legal
                requirements)
              </li>
              <li>
                <strong>Retained:</strong> Anonymized analytics data and
                legal/tax records as required by law
              </li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AccountDeletion;
